import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { documentsApi, foldersApi } from '../../lib/api';
import type { Folder, KbArticle, Well } from '../../lib/types';

/**
 * What the assistant holds for a company or a well, drawn as it is used: a
 * brain in the middle, and everything stored for it in orbit around it — the
 * knowledge written here, the documents filed on the well, the folders they
 * are in and the tags that say what data they hold.
 *
 * Every word is something that exists. Nothing is invented to fill the sky:
 * a well with nothing on it shows a brain alone, and says so.
 *
 * The motion is CSS only. Each word rides two animations, one across and one
 * up and down a quarter turn apart, which together are an ellipse; a third
 * shrinks and dims it on the far half of the turn and drops it behind the
 * brain. Positions come from the word itself, so the sky does not reshuffle
 * each time the page draws. With reduced motion the words stand still where
 * their orbit would have started.
 */

type Kind = 'article' | 'doc' | 'tag' | 'folder' | 'well';

interface Word {
  kind: Kind;
  text: string;
  /** Set on an article: clicking it opens the article in the editor below. */
  articleId?: number;
  title: string;
}

interface Props {
  /** Whose knowledge is shown, for the caption. */
  name: string;
  /** The well, when this is a well's shelf; null for a company's. */
  well: Well | null;
  /** The company's wells, shown when this is the company's shelf. */
  wells: Well[];
  /** The shelf's articles as last read; null until they have been. */
  articles: KbArticle[] | null;
  onOpenArticle: (id: number) => void;
}

/** How many of each kind are put in orbit; more than this is a blur. */
const LIMIT: Record<Kind, number> = { article: 14, doc: 22, tag: 12, folder: 9, well: 12 };

const LEGEND: { kind: Kind; label: string }[] = [
  { kind: 'article', label: 'Knowledge' },
  { kind: 'doc', label: 'Documents' },
  { kind: 'tag', label: 'Data tags' },
  { kind: 'folder', label: 'Folders' },
  { kind: 'well', label: 'Wells' },
];

/** A stable number in [0, 1) from a string, so a word keeps its orbit. */
function seeded(text: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < text.length; i += 1) {
    h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

function short(text: string, max = 30) {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

function flatten(folders: Folder[]): Folder[] {
  return folders.flatMap((f) => [f, ...flatten(f.children ?? [])]);
}

/** What is filed on one well: its folders that hold documents, some of the documents, and its tags. */
function useWellHoldings(wellId: number | null) {
  const [held, setHeld] = useState<{ for: number; folders: Folder[]; docs: string[]; tags: string[] } | null>(null);

  useEffect(() => {
    if (wellId == null) return;
    let cancelled = false;

    (async () => {
      // Each of the three is optional: a role without the Documents menu is
      // refused them, and the sky is then just the knowledge.
      const tree = await foldersApi.tree(wellId).catch(() => null);
      const folders = flatten(tree?.items ?? [])
        .filter((f) => f.kind === 'folder' && f.documentCount > 0)
        .sort((a, b) => b.documentCount - a.documentCount);

      const [tags, ...pages] = await Promise.all([
        documentsApi.tags(wellId).catch(() => null),
        // A few names from the fullest folders: enough to recognise the well
        // by, without reading every folder to draw a picture.
        ...folders.slice(0, 4).map((f) => documentsApi.list(f.id, 1, 8).catch(() => null)),
      ]);

      if (cancelled) return;
      setHeld({
        for: wellId,
        folders,
        docs: pages.flatMap((p) => p?.items.map((d) => d.name) ?? []),
        tags: tags?.items.map((t) => t.tag) ?? [],
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [wellId]);

  return held && held.for === wellId ? held : null;
}

export function KnowledgeSpace({ name, well, wells, articles, onOpenArticle }: Props) {
  const held = useWellHoldings(well?.id ?? null);

  const words = useMemo<Word[]>(() => {
    const out: Word[] = [];
    const add = (kind: Kind, items: Omit<Word, 'kind'>[]) =>
      out.push(...items.slice(0, LIMIT[kind]).map((w) => ({ kind, ...w })));

    add(
      'article',
      (articles ?? []).map((a) => ({ text: a.title, articleId: a.id, title: `Knowledge: ${a.title} — open it` })),
    );
    if (well) {
      add('tag', (held?.tags ?? []).map((t) => ({ text: t, title: `Data tag: ${t}` })));
      add('doc', (held?.docs ?? []).map((d) => ({ text: d.replace(/\.[a-z0-9]{2,5}$/i, ''), title: `Document: ${d}` })));
      add(
        'folder',
        (held?.folders ?? []).map((f) => ({
          text: f.name,
          title: `Folder: ${f.name} — ${f.documentCount} document${f.documentCount === 1 ? '' : 's'}`,
        })),
      );
    } else {
      add('well', wells.map((w) => ({ text: w.name, title: `Well: ${w.name}` })));
    }
    return out;
  }, [articles, held, well, wells]);

  const documents = (held?.folders ?? []).reduce((n, f) => n + f.documentCount, 0);
  const loading = articles === null || (well !== null && held === null);

  const counts = [
    `${articles?.length ?? 0} knowledge article${articles?.length === 1 ? '' : 's'}`,
    ...(well
      ? [`${documents} document${documents === 1 ? '' : 's'}`, `${held?.tags.length ?? 0} data tag${held?.tags.length === 1 ? '' : 's'}`]
      : [`${wells.length} well${wells.length === 1 ? '' : 's'}`]),
  ];

  const kinds = new Set(words.map((w) => w.kind));

  return (
    <section className="kspace" aria-label={`What the assistant holds for ${name}`}>
      <div className="kspace-stars" aria-hidden="true" />

      <header className="kspace-head">
        <strong>What the assistant holds for {name}</strong>
        <span>{loading ? 'Reading…' : counts.join(' · ')}</span>
      </header>

      <div className="kspace-sky">
        <Brain />

        {words.map((w, i) => {
          const key = `${w.kind}:${w.text}`;
          // Three rings, so words pass each other instead of queueing; the
          // ring, the pace and the place on it all come from the word.
          const ring = i % 3;
          const period = 26 + ring * 9 + seeded(key, 1) * 10;
          // Spread evenly round each ring to begin with, then nudged, so the
          // sky opens balanced rather than with everything on one side.
          const perRing = Math.ceil(words.length / 3);
          const turn = ((Math.floor(i / 3) + ring / 3) / perRing + seeded(key, 2) * 0.08) % 1;
          const phase = turn * period;
          const angle = (phase / period) * Math.PI * 2;
          const rx = 19 + ring * 9 + seeded(key, 3) * 4;
          const ry = 22 + ring * 9 + seeded(key, 4) * 5;

          const style = {
            '--rx': rx,
            '--ry': ry,
            '--t': `${period}s`,
            '--d': `${-phase}s`,
            '--dx': `${-(phase + period / 4)}s`,
            // Where the orbit starts, for when nothing moves.
            '--x0': rx * Math.sin(angle),
            '--y0': -ry * Math.cos(angle),
          } as CSSProperties;

          const label = short(w.text);
          return (
            <span key={key} className={`kword kword--${w.kind}`} style={style}>
              <span className="kword-y">
                <span className="kword-z">
                  {w.articleId !== undefined ? (
                    <button type="button" title={w.title} onClick={() => onOpenArticle(w.articleId as number)}>
                      {label}
                    </button>
                  ) : (
                    <span title={w.title}>{label}</span>
                  )}
                </span>
              </span>
            </span>
          );
        })}

        {!loading && words.length === 0 && (
          <p className="kspace-empty">
            Nothing is stored for {name} yet. What is written below, and every document filed and
            tagged on the well, will circle here.
          </p>
        )}
      </div>

      <ul className="kspace-legend" aria-label="What the colours mean">
        {LEGEND.filter((l) => kinds.has(l.kind)).map((l) => (
          <li key={l.kind} className={`kword--${l.kind}`}>
            <i aria-hidden="true" />
            {l.label}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The brain: two hemispheres seen from above, with the folds drawn as light
 * running through them. Drawn here rather than loaded, so it takes the
 * panel's colours and needs no request.
 */
function Brain() {
  // One hemisphere; the other is the same shape mirrored.
  const lobe =
    'M-4 -58 C-30 -66 -58 -52 -62 -28 C-78 -18 -80 8 -66 20 C-70 40 -52 60 -30 56 C-20 66 -8 64 -4 56 Z';
  const folds = [
    'M-14 -50 C-30 -52 -46 -42 -44 -26 C-43 -16 -34 -12 -26 -17',
    'M-60 -16 C-48 -12 -40 -2 -46 9 C-50 17 -60 16 -63 10',
    'M-9 -32 C-20 -28 -23 -15 -14 -8 C-8 -3 -10 7 -19 9',
    'M-54 30 C-43 22 -30 26 -30 36 C-30 45 -39 49 -45 44',
    'M-9 20 C-18 22 -23 32 -16 40 C-12 46 -14 52 -21 53',
    'M-36 -5 C-29 1 -29 12 -36 17',
    'M-30 -38 C-24 -34 -22 -28 -26 -24',
    'M-50 -32 C-54 -26 -54 -20 -50 -16',
    'M-24 22 C-30 16 -38 16 -42 20',
  ];

  return (
    <svg className="kbrain" viewBox="-100 -90 200 180" aria-hidden="true">
      <defs>
        <radialGradient id="kbrain-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7df3e1" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#3aa8ff" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#3aa8ff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="kbrain-fill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8ff5e6" />
          <stop offset="55%" stopColor="#4fbaac" />
          <stop offset="100%" stopColor="#2f6fb5" />
        </linearGradient>
      </defs>

      <circle className="kbrain-halo" r="88" fill="url(#kbrain-glow)" />

      {[1, -1].map((side) => (
        <g key={side} transform={`scale(${side} 1)`}>
          <path d={lobe} fill="url(#kbrain-fill)" stroke="#d9fffa" strokeWidth="1.6" strokeLinejoin="round" />
          {folds.map((d, i) => (
            <g key={d} fill="none" strokeLinecap="round">
              {/* The fold itself, and a spark of light running along it. */}
              <path d={d} stroke="#0d4558" strokeOpacity="0.6" strokeWidth="2" />
              <path
                className="kbrain-fold"
                d={d}
                stroke="#f2fffd"
                strokeWidth="2"
                style={{ animationDelay: `${(-(i * 0.47) - (side === 1 ? 0 : 1.3)).toFixed(2)}s` }}
              />
            </g>
          ))}
        </g>
      ))}
    </svg>
  );
}
